import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import Overview from "./Overview";
import ArSong from "./ArSong";
import TranslatedSong from "./TranslatedSong";
import SpanishTranslatedSong from "./SpanishTranslatedSong";
import TransliteratedSong from "./TransliteratedSong";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "../config/api";

const SongDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("overview");
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to check if text contains only English characters
  const isEnglishText = (text) => {
    const englishRegex = /^[A-Za-z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/\?\s]*$/;
    const hasEnglishLetter = /[A-Za-z]/.test(text);
    return englishRegex.test(text) && hasEnglishLetter;
  };

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    setSong(null);

    const fetchSongAndIncrementViews = async () => {
      try {
        // Load song first so Mongo outage / slow view-counter never blocks the page
        const response = await fetch(
          `${API_BASE_URL}/api/ArbSongs/getArbSongBySongID/${id}`
        );
        let data;
        try {
          data = await response.json();
        } catch {
          throw new Error(t("error.general"));
        }
        if (cancelled) return;

        if (response.ok) {
          setSong(data);
        } else {
          throw new Error(data.msg || t("error.general"));
        }

        fetch(`${API_BASE_URL}/api/ArbSongs/incrementViews/${id}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }).catch(() => {});
      } catch (err) {
        console.error("Error in fetchSongAndIncrementViews:", err);
        if (!cancelled) setError(t("error.general"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSongAndIncrementViews();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ClipLoader color="#ffbb02" size={50} />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  if (!song) {
    return (
      <div className="text-gray-600 p-4">{t("error.general")}</div>
    );
  }

  const isEnglishSong = isEnglishText(song?.title || "");

  const tabs = isEnglishSong
    ? [
        { id: "overview", label: t("song.overview") },
      ]
    : [
        { id: "overview", label: t("song.overview") },
        { id: "arabic", label: t("song.arabic") },
        { id: "translated", label: t("song.translated") },
        { id: "spanish", label: t("song.spanish") || "Spanish" },
        { id: "transliterated", label: t("song.transliterated") },
      ];

  return (
    <div className="flex flex-col w-full min-h-screen bg-white z-0">
      {/* Tabs navigation */}
      <div className="w-full px-4 mt-8 sm:mt-16">
        <div className="max-w-7xl mx-auto">
          {/* Scrollable tabs container */}
          <div className="flex overflow-x-auto gap-6 sm:gap-8 md:gap-10 justify-start sm:justify-center pb-2 hide-scrollbar pt-[50px]">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`whitespace-nowrap px-2 py-1 text-sm sm:text-base md:text-lg transition-colors
                  ${
                    activeTab === tab.id
                      ? "text-black font-bold border-b-2 border-black"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="mt-6 px-0 sm:px-6 md:px-8">
            {activeTab === "overview" && <Overview song={song} />}
            {!isEnglishSong && activeTab === "arabic" && <ArSong song={song} />}
            {!isEnglishSong && activeTab === "translated" && (
              <TranslatedSong song={song} />
            )}
            {!isEnglishSong && activeTab === "spanish" && (
              <SpanishTranslatedSong song={song} />
            )}
            {!isEnglishSong && activeTab === "transliterated" && (
              <TransliteratedSong song={song} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SongDetail;